import React, { useMemo } from 'react';
import Icon from 'carbon-react/lib/components/icon';
import { IconType } from 'carbon-react/lib/components/icon';
import { FormattedMessage, useIntl } from 'react-intl';
import Button from 'carbon-react/lib/components/button';

import * as styles from './sync-result-banner.scss';
import { SyncResultType } from '@futrli/predict';

interface SyncResultBannerProps {
  type: SyncResultType;
  onViewDetails: () => void;
  onClose: () => void;
}

export const SyncResultBanner = ({ type, onViewDetails, onClose }: SyncResultBannerProps) => {
  const intl = useIntl();

  const syncConfig: Record<
    SyncResultType,
    { className: string; viewButtonClassName: string; iconType: IconType; message: string }
  > = useMemo(
    () => ({
      success: {
        className: 'success',
        viewButtonClassName: 'viewDetailsButton',
        iconType: 'tick_circle',
        message: intl.formatMessage({
          id: 'common.sync.data_synced_successfully',
          defaultMessage: 'Data synced successfully'
        })
      },
      partial: {
        className: 'partial',
        iconType: 'warning',
        viewButtonClassName: 'viewDetailsButtonBlack',
        message: intl.formatMessage({
          id: 'common.sync.data_sync_partially_complete',
          defaultMessage: 'Data sync is partially complete'
        })
      },
      failed: {
        className: 'failed',
        viewButtonClassName: 'viewDetailsButton',
        iconType: 'error',
        message: intl.formatMessage({
          id: 'common.sync.data_sync_failed',
          defaultMessage: 'Data sync failed'
        })
      }
    }),
    [intl]
  );

  const config = syncConfig[type];

  return (
    <div className={`${styles.root} ${styles[config.className]}`} role="status">
      <div className={styles.container}>
        <div className={styles.inner}>
          <div className={styles.content}>
            <Icon type={config.iconType} color="currentColor" />
            <span className={styles.message}>{config.message}</span>
            <Button
              buttonType="secondary"
              className={styles[config.viewButtonClassName]}
              onClick={onViewDetails}
              size="small"
            >
              <FormattedMessage id="common.viewDetails" defaultMessage="View Details" />
            </Button>
          </div>
          <div onClick={onClose} style={{ cursor: 'pointer' }} data-testid="close-sync-banner-button">
            <Icon type="cross" color="currentColor" />
          </div>
        </div>
      </div>
    </div>
  );
};
